import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { XRPC } from '@atcute/client';
import {
  OAuthUserAgent,
  configureOAuth,
  createAuthorizationUrl,
  deleteStoredSession,
  finalizeAuthorization,
  getSession,
  listStoredSessions,
  resolveFromIdentity,
} from '@atcute/oauth-browser-client';
import type { ActorProfile } from '../domain/types';
import { recordSkyspaceActivity } from '../data/site-services';
import {
  cleanOAuthCallbackUrl,
  modernApplicationPath,
  modernOAuthRedirectUri,
  oauthCallbackParams,
  OAUTH_RETURN_PATH_KEY,
} from './oauth-callback';
import { dashboardExperimentEnabled } from '../features/home/experiment';

const OAUTH_SCOPE = 'atproto transition:generic transition:chat.bsky';
let callbackPromise: Promise<OAuthUserAgent> | undefined;

if (typeof window !== 'undefined') {
  configureOAuth({
    metadata: {
      client_id: `${window.location.origin}/client-metadata.json`,
      redirect_uri: modernOAuthRedirectUri(
        window.location.origin,
        window.location.pathname,
      ),
    },
    storageName: 'atcute-oauth',
  });
}

export interface AuthState {
  agent?: OAuthUserAgent;
  rpc?: XRPC;
  did?: string;
  handle?: string;
  avatar?: string;
  pending: boolean;
  error?: string;
  login(handle: string): Promise<void>;
  logout(): Promise<void>;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

async function profileFor(rpc: XRPC, actor: string): Promise<ActorProfile> {
  const response = await rpc.request({
    type: 'get',
    nsid: 'app.bsky.actor.getProfile',
    params: { actor },
  });
  return response.data as ActorProfile;
}

function agentForSession(session: Awaited<ReturnType<typeof getSession>>): OAuthUserAgent {
  return new OAuthUserAgent(session);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [agent, setAgent] = useState<OAuthUserAgent>();
  const [profile, setProfile] = useState<ActorProfile>();
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let disposed = false;
    const restore = async () => {
      const callbackParams = oauthCallbackParams(
        window.location.hash,
        window.location.search,
      );
      try {
        let restoredAgent: OAuthUserAgent | undefined;
        if (callbackParams) {
          callbackPromise ||= finalizeAuthorization(callbackParams).then(agentForSession);
          restoredAgent = await callbackPromise;
          if (!disposed) {
            history.replaceState(null, '', cleanOAuthCallbackUrl(window.location.pathname));
            localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
          }
        } else {
          const did = listStoredSessions()[0];
          if (did) restoredAgent = agentForSession(await getSession(did, { allowStale: true }));
        }
        if (!restoredAgent || disposed) return;
        const rpc = new XRPC({ handler: restoredAgent });
        const actor = await profileFor(rpc, restoredAgent.sub);
        if (disposed) return;
        setAgent(restoredAgent);
        setProfile(actor);
        localStorage.setItem('bskyHandle', actor.handle);
        recordSkyspaceActivity(restoredAgent.sub, actor.handle, 'login');
        void import('../private-state/runtime').then(({ startPrivateStateSync }) => (
          startPrivateStateSync(rpc, restoredAgent.sub)
        ));
        if (window.location.hash || window.location.search === '') {
          const destination = dashboardExperimentEnabled()
            ? 'home=feed'
            : encodeURIComponent(actor.handle);
          history.replaceState(null, '', `${window.location.pathname}?${destination}`);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      } catch (reason) {
        if (!disposed) {
          if (callbackParams) {
            history.replaceState(null, '', cleanOAuthCallbackUrl(window.location.pathname));
            localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
          }
          setError(reason instanceof Error ? reason.message : 'Could not restore your Bluesky session.');
        }
      } finally {
        if (!disposed) setPending(false);
      }
    };
    void restore();
    return () => {
      disposed = true;
    };
  }, []);

  const login = useCallback(async (rawHandle: string) => {
    setError(undefined);
    try {
      const handle = rawHandle.trim().replace(/^@/, '');
      if (!handle) throw new Error('Enter your Bluesky handle first.');
      const normalized = handle.includes('.') ? handle : `${handle}.bsky.social`;
      localStorage.setItem(
        OAUTH_RETURN_PATH_KEY,
        modernApplicationPath(window.location.pathname),
      );
      const { identity, metadata } = await resolveFromIdentity(normalized);
      const url = await createAuthorizationUrl({
        metadata,
        identity,
        scope: OAUTH_SCOPE,
      });
      await new Promise(resolve => window.setTimeout(resolve, 200));
      window.location.assign(url);
    } catch (reason) {
      localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
      setError(reason instanceof Error ? reason.message : 'Could not start Bluesky sign-in.');
      throw reason;
    }
  }, []);

  const logout = useCallback(async () => {
    setPending(true);
    try {
      if (agent) await agent.signOut();
    } catch {
      if (agent) deleteStoredSession(agent.sub);
    } finally {
      localStorage.removeItem('bskyHandle');
      void import('../private-state/runtime').then(({ stopPrivateStateSync }) => {
        stopPrivateStateSync();
      });
      setAgent(undefined);
      setProfile(undefined);
      setPending(false);
    }
  }, [agent]);

  const value = useMemo<AuthState>(() => ({
    agent,
    rpc: agent ? new XRPC({ handler: agent }) : undefined,
    did: agent?.sub,
    handle: profile?.handle,
    avatar: profile?.avatar,
    pending,
    error,
    login,
    logout,
  }), [agent, error, login, logout, pending, profile?.avatar, profile?.handle]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
